/**
 * MyEnquiriesWidget — Dashboard summary showing the current user's
 * open + overdue enquiry counts and the unassigned queue.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { MessageCircle, ArrowRight, AlertCircle, Inbox } from "lucide-react";


export default function MyEnquiriesWidget() {
  const [counters, setCounters] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/enquiries/counters");
        if (!cancelled) setCounters(r.data || {});
      } catch {
        if (!cancelled) setCounters({});
      }
    })();
  }, []);

  if (!counters) return null;
  const total = (counters.mine_open || 0) + (counters.unassigned || 0) + (counters.urgent_open || 0);
  if (total === 0) return null;

  return (
    <Card
      className="p-4 rounded-lg border border-zinc-200 shadow-none"
      data-testid="dashboard-enquiries-widget"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-zinc-700" />
          <div className="text-sm font-semibold text-zinc-950">Enquiries</div>
        </div>
        <Link
          to="/enquiries"
          className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
          data-testid="dashboard-enquiries-open"
        >
          Open enquiries <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Link
          to="/enquiries?view=mine_open"
          className="p-2 rounded-md border border-zinc-200 hover:border-zinc-400 transition-colors"
          data-testid="dashboard-enquiries-mine"
        >
          <div className="text-xl font-semibold text-zinc-950 font-mono-num">{counters.mine_open || 0}</div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Inbox className="h-3 w-3" /> My open</div>
        </Link>
        <Link
          to="/enquiries?view=overdue"
          className={`p-2 rounded-md border transition-colors ${counters.mine_overdue > 0 ? "border-red-300 bg-red-50" : "border-zinc-200 hover:border-zinc-400"}`}
          data-testid="dashboard-enquiries-overdue"
        >
          <div className="text-xl font-semibold text-zinc-950 font-mono-num">{counters.mine_overdue || 0}</div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Overdue</div>
        </Link>
        <Link
          to="/enquiries?view=unassigned"
          className={`p-2 rounded-md border transition-colors ${counters.unassigned > 0 ? "border-amber-300 bg-amber-50" : "border-zinc-200 hover:border-zinc-400"}`}
          data-testid="dashboard-enquiries-unassigned"
        >
          <div className="text-xl font-semibold text-zinc-950 font-mono-num">{counters.unassigned || 0}</div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500">Unassigned</div>
        </Link>
      </div>
    </Card>
  );
}
